import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Container,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';
import { FranchisesTable } from '../../components/FranchisesTable';
import { FranchiseDetailDialog } from '../../components/FranchiseDetailDialog';
import { BrandFilterBar, BrandMetrics } from '../../components/BrandFilterBar';

// Zone components
import {
  VitalSignsRibbon,
  AUVTrendChart,
  TimeToFirstDollarChart,
  SOPAdoptionHeatMap,
  LeadConversionFunnel,
  LedgerSyncStatus,
  ARAgingTable,
} from '../../components/franchise-dashboard';

// Mock data
import {
  getMockSWSDelta,
  getMockActiveWorkOrders,
  getMockNetworkNPS,
  getMockRoyaltyIntegrityDelta,
  getMockAUVTrend,
  getMockOnboardingData,
  getMockLeadFunnelData,
  getMockLedgerSyncData,
  getMockARAgingData,
  getMockBrandSales,
  resolveMockBrandKey,
} from '../../components/franchise-dashboard/mockData';

// Types
interface EnterpriseMetrics {
  enterprise: {
    id: string;
    name: string;
    description?: string;
  };
  summary: {
    totalBrands: number;
    totalRegions: number;
    totalFranchisees: number;
    averageComplianceScore: number | null;
    averageGrowthRate: number | null;
  };
  revenue: {
    totalRevenue: number;
    totalRoyalties: number;
    averageRoyaltyRate: number | null;
  };
  invoices: {
    total: number;
    paid: number;
    totalRevenue: number;
  };
}

// Brand color mapping for known demo brands
const BRAND_COLORS: Record<string, string> = {
  AutoFix: '#D32F2F',
  CleanCo: '#0066CC',
  LawnPros: '#2E7D32',
  PetCare: '#7B1FA2',
};

// Main Dashboard Component
export const EnterpriseDashboard = () => {
  const [enterpriseId, setEnterpriseId] = useState<string>('');
  const [enterpriseName, setEnterpriseName] = useState<string>('');
  const [metrics, setMetrics] = useState<EnterpriseMetrics | null>(null);
  const [brandsList, setBrandsList] = useState<any[]>([]);
  const [brandsAnalytics, setBrandsAnalytics] = useState<Record<string, any>>({});
  const [regionsList, setRegionsList] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [allFranchisees, setAllFranchisees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedRegionId, setSelectedRegionId] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFranchisee, setSelectedFranchisee] = useState<any | null>(null);
  const dataProvider = useDataProvider();

  // Fetch first enterprise, its metrics, and brands on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch enterprises list
        const enterpriseResult = await dataProvider.getList('enterprises', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });

        if (enterpriseResult.data && enterpriseResult.data.length > 0) {
          const enterprise = enterpriseResult.data[0];
          setEnterpriseId(enterprise.id);
          setEnterpriseName(enterprise.name);

          // Fetch metrics for the first enterprise
          try {
            const metricsResult = await dataProvider.getOne('franchise-analytics/enterprise', {
              id: enterprise.id,
            });
            setMetrics(metricsResult.data);
          } catch (metricsError) {
            console.error('Error fetching metrics:', metricsError);
            // Set mock data for demo
            setMetrics({
              enterprise: { id: enterprise.id, name: enterprise.name },
              summary: {
                totalBrands: 4,
                totalRegions: 12,
                totalFranchisees: 40,
                averageComplianceScore: 91.5,
                averageGrowthRate: 5.2,
              },
              revenue: {
                totalRevenue: 7250000,
                totalRoyalties: 435000,
                averageRoyaltyRate: 0.06,
              },
              invoices: {
                total: 856,
                paid: 413,
                totalRevenue: 7250000,
              },
            });
          }

          // Fetch brands for the enterprise
          let fetchedBrands: any[] = [];
          try {
            const brandsResult = await dataProvider.getList('brands', {
              pagination: { page: 1, perPage: 100 },
              sort: { field: 'name', order: 'ASC' },
              filter: { enterpriseId: enterprise.id },
            });
            fetchedBrands = brandsResult.data || [];
            setBrandsList(fetchedBrands);
          } catch (brandsError) {
            console.error('Error fetching brands:', brandsError);
            setBrandsList([]);
          }

          // Fetch per-brand analytics (revenue from invoices) in parallel
          if (fetchedBrands.length > 0) {
            try {
              const analyticsResults = await Promise.allSettled(
                fetchedBrands.map((brand: any) =>
                  dataProvider.getOne('franchise-analytics/brand', { id: brand.id }),
                ),
              );
              const analyticsMap: Record<string, any> = {};
              analyticsResults.forEach((result, i) => {
                if (result.status === 'fulfilled') {
                  analyticsMap[fetchedBrands[i].id] = result.value.data;
                }
              });
              setBrandsAnalytics(analyticsMap);
            } catch (analyticsError) {
              console.error('Error fetching brand analytics:', analyticsError);
            }
          }

          // Fetch all franchisees (tenants) for the enterprise — kept unfiltered for brand bar metrics
          try {
            const franchiseesResult = await dataProvider.getList('tenants', {
              pagination: { page: 1, perPage: 1000 },
              sort: { field: 'code', order: 'ASC' },
              filter: { enterpriseId: enterprise.id },
            });
            const all = franchiseesResult.data || [];
            setFranchisees(all);
            setAllFranchisees(all);
          } catch (franchiseesError) {
            console.error('Error fetching franchisees:', franchiseesError);
            setFranchisees([]);
            setAllFranchisees([]);
          }
        }
      } catch (error) {
        console.error('Error fetching enterprises:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dataProvider]);

  // Fetch regions when brand is selected
  useEffect(() => {
    if (!selectedBrandId || !enterpriseId) {
      setRegionsList([]);
      setSelectedRegionId('');
      return;
    }

    const fetchRegions = async () => {
      try {
        const regionsResult = await dataProvider.getList('regions', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { brandId: selectedBrandId },
        });
        setRegionsList(regionsResult.data || []);
      } catch (error) {
        console.error('Error fetching regions:', error);
        setRegionsList([]);
      }
    };

    fetchRegions();
  }, [selectedBrandId, enterpriseId, dataProvider]);

  // Update franchisees when filters change
  useEffect(() => {
    if (!enterpriseId) {
      return;
    }

    const fetchFranchisees = async () => {
      try {
        const filter: any = { enterpriseId };

        if (selectedBrandId) {
          filter.brandId = selectedBrandId;
        }

        if (selectedRegionId) {
          filter.regionId = selectedRegionId;
        }

        const result = await dataProvider.getList('tenants', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'code', order: 'ASC' },
          filter,
        });

        setFranchisees(result.data || []);
      } catch (error) {
        console.error('Error fetching franchisees:', error);
        setFranchisees([]);
      }
    };

    fetchFranchisees();
  }, [enterpriseId, selectedBrandId, selectedRegionId, dataProvider]);

  // Compute per-brand metrics — revenue/royalties from analytics API, compliance/growth from franchisees.
  // Must be above any early returns to satisfy Rules of Hooks.
  const brandMetrics: BrandMetrics[] = useMemo(() => {
    return brandsList.map((brand: any) => {
      const analytics = brandsAnalytics[brand.id];

      // Revenue & royalties come from the analytics endpoint (sums paid invoices)
      const totalRevenue = analytics?.revenue?.totalRevenue || 0;
      const totalRoyalties = analytics?.revenue?.totalRoyalties || 0;

      // Compliance & growth come from tenant fields
      const avgCompliance = analytics?.summary?.averageComplianceScore ?? (() => {
        const brandFranchisees = allFranchisees.filter((f: any) => f.brandId === brand.id);
        return brandFranchisees.length > 0
          ? brandFranchisees.reduce((sum: number, f: any) => sum + (f.complianceScore || 0), 0) /
              brandFranchisees.length
          : 0;
      })();

      const avgGrowthRate = analytics?.summary?.averageGrowthRate ?? (() => {
        const brandFranchisees = allFranchisees.filter((f: any) => f.brandId === brand.id);
        return brandFranchisees.length > 0
          ? brandFranchisees.reduce((sum: number, f: any) => sum + (f.growthRate || 0), 0) /
              brandFranchisees.length
          : 0;
      })();

      const brandPrimaryColor =
        BRAND_COLORS[brand.name] ||
        brand.branding?.primaryColor ||
        '#1976d2';

      return {
        id: brand.id,
        name: brand.name,
        color: brandPrimaryColor,
        revenue: totalRevenue,
        royalties: totalRoyalties,
        compliance: Math.round(Number(avgCompliance) * 10) / 10,
        growthRate: Math.round(Number(avgGrowthRate) * 10) / 10,
      };
    });
  }, [brandsList, brandsAnalytics, allFranchisees]);

  if (!enterpriseId || !enterpriseName) {
    return (
      <Box sx={{ p: 3 }}>
        <PageTitle
          title="Dashboard"
          subtitle="Franchise network performance & analytics"
          icon={<BusinessIcon />}
        />
        <CircularProgress />
      </Box>
    );
  }

  // ─── Computed Metrics ─────────────────────────────────────────

  // Selected brand name from the brand filter bar (may include extra label text)
  const activeBrandName = selectedBrandId
    ? brandsList.find((b: any) => b.id === selectedBrandId)?.name || ''
    : '';
  const activeMockBrandKey = resolveMockBrandKey(activeBrandName || undefined);

  const mockSales = getMockBrandSales(activeMockBrandKey);

  // Derive revenue from brandMetrics (same source as the brand bar) so vital signs always match.
  // When a brand is selected use that brand's metric; otherwise sum all brands.
  const activeBrandMetric = selectedBrandId ? brandMetrics.find(b => b.id === selectedBrandId) : null;
  const brandBarRevenue = activeBrandMetric
    ? activeBrandMetric.revenue
    : brandMetrics.reduce((sum, b) => sum + b.revenue, 0);
  // Apply the same ×100 display scale used by the brand bar tiles
  const BRAND_DISPLAY_SCALE = 100;
  const systemWideSales = brandBarRevenue > 0 ? brandBarRevenue * BRAND_DISPLAY_SCALE : mockSales.revenue;

  // Royalty Integrity Score: (collected / expected) * 100
  const royaltyRate = metrics?.revenue?.averageRoyaltyRate || 0.06;
  const brandBarRoyalties = activeBrandMetric
    ? activeBrandMetric.royalties
    : brandMetrics.reduce((sum, b) => sum + b.royalties, 0);
  const expectedRoyalties = systemWideSales * royaltyRate;
  const royaltyIntegrityScore =
    brandBarRevenue > 0 && expectedRoyalties > 0
      ? Math.min(Math.round((brandBarRoyalties / expectedRoyalties) * 100), 100)
      : mockSales.royaltyScore;

  // Keep realRevenue for AUV calculation (unchanged from before)
  const activeBrandAnalytics = selectedBrandId ? brandsAnalytics[selectedBrandId] : null;
  const realRevenue = activeBrandAnalytics
    ? (activeBrandAnalytics.revenue?.totalRevenue || 0)
    : (metrics?.revenue?.totalRevenue || 0);

  // Mock data — brand-specific when a brand is selected
  const swsDelta = getMockSWSDelta(activeMockBrandKey);
  const activeWorkOrders = getMockActiveWorkOrders(activeMockBrandKey);
  const networkNPS = getMockNetworkNPS(activeMockBrandKey);
  const royaltyDelta = getMockRoyaltyIntegrityDelta(activeMockBrandKey);

  // AUV — scale mock trend based on real revenue, and pass real current AUV as the latest point
  const mockAUVBase = getMockAUVTrend();
  // Compute real current AUV for the selected brand (or whole network)
  const auvFranchiseeCount = activeBrandAnalytics
    ? Math.max(activeBrandAnalytics.summary?.totalFranchisees || 1, 1)
    : Math.max(metrics?.summary?.totalFranchisees || 40, 1);
  const realCurrentAUV = realRevenue > 0 ? Math.round(realRevenue / auvFranchiseeCount) : undefined;
  // Scale the entire trend line proportionally so the shape is preserved but the Y axis reflects reality
  const auvScaleRatio =
    realCurrentAUV && mockAUVBase.length > 0
      ? realCurrentAUV / mockAUVBase[mockAUVBase.length - 1].auv
      : 1;
  const auvTrendDataRaw = auvScaleRatio !== 1
    ? mockAUVBase.map((p) => ({ ...p, auv: Math.round(p.auv * auvScaleRatio), target: Math.round(p.target * auvScaleRatio) }))
    : mockAUVBase;
  // Apply ×1000 display scale to match revenue scale (chart shows $27M not $27K)
  const AUV_DISPLAY_SCALE = 1000;
  const auvTrendData = auvTrendDataRaw.map((p) => ({
    ...p,
    auv: p.auv * AUV_DISPLAY_SCALE,
    target: p.target * AUV_DISPLAY_SCALE,
  }));
  const auvCurrentForChart = realCurrentAUV ? realCurrentAUV * AUV_DISPLAY_SCALE : undefined;

  // Onboarding: filter to selected brand's entries (matched by brand name prefix)
  const allOnboardingData = getMockOnboardingData();
  const onboardingData = activeMockBrandKey
    ? allOnboardingData.filter((e) => e.franchisee.startsWith(activeMockBrandKey))
    : allOnboardingData;

  // Lead funnel: brand-specific data when brand is selected
  const leadFunnelData = getMockLeadFunnelData(activeMockBrandKey);

  // Ledger sync: brand-specific data with accurate sync percentages
  const ledgerSyncData = getMockLedgerSyncData(activeMockBrandKey);

  // AR aging: filter rows to selected brand
  const allArAgingData = getMockARAgingData();
  const arAgingData = activeMockBrandKey
    ? allArAgingData.filter((e) => e.franchisee.startsWith(activeMockBrandKey))
    : allArAgingData;

  // Map franchisees for SOP heat map (uses real compliance data — already filtered by brand via state)
  const sopFranchisees = franchisees.map((f: any) => ({
    code: f.code,
    name: f.name || f.code,
    complianceScore: f.complianceScore ?? 0,
    franchiseeOwner: f.franchiseeOwner,
  }));

  return (
    <Box>
      <PageTitle
        title={enterpriseName}
        subtitle="FranchiseOS Network Command Center"
        icon={<BusinessIcon />}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : metrics ? (
        <>
          {/* Brand Filter Bar — always at the top */}
          {brandMetrics.length > 0 && (
            <BrandFilterBar
              brands={brandMetrics}
              selectedBrandId={selectedBrandId}
              onBrandSelect={setSelectedBrandId}
            />
          )}

          <Card sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Command Center" />
              <Tab label="Franchises" />
            </Tabs>
          </Card>

          {activeTab === 0 && (
            <>
              {/* ═══ ZONE 1: VITAL SIGNS ═══ */}
              <VitalSignsRibbon
                systemWideSales={systemWideSales}
                swsDelta={swsDelta}
                royaltyIntegrityScore={royaltyIntegrityScore}
                royaltyDelta={royaltyDelta}
                activeWorkOrders={activeWorkOrders}
                networkNPS={networkNPS}
              />

              {/* ═══ ZONE 2 + ZONE 3: Center Row ═══ */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Zone 2: Performance & Growth (Left) */}
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '1.5px',
                      color: 'text.disabled',
                      mb: 1.5,
                    }}
                  >
                    Performance & Growth
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <AUVTrendChart data={auvTrendData} currentAUV={auvCurrentForChart} />
                    <TimeToFirstDollarChart data={onboardingData} />
                  </Box>
                </Grid>

                {/* Zone 3: Compliance & SOP Health (Right) */}
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '1.5px',
                      color: 'text.disabled',
                      mb: 1.5,
                    }}
                  >
                    Compliance & SOP Health
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <SOPAdoptionHeatMap franchisees={sopFranchisees} />
                    <LeadConversionFunnel data={leadFunnelData} />
                  </Box>
                </Grid>
              </Grid>

              {/* ═══ ZONE 4: FINANCIAL GOVERNANCE ═══ */}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  color: 'text.disabled',
                  mb: 1.5,
                }}
              >
                Financial Governance
              </Typography>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <LedgerSyncStatus data={ledgerSyncData} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ARAgingTable data={arAgingData} />
                </Grid>
              </Grid>
            </>
          )}

          {activeTab === 1 && (
            <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
              {/* Franchises Tab */}
              <FranchisesTable
                franchisees={franchisees}
                showBrand={true}
                showRegion={true}
                showFilters={true}
                brands={brandsList.map((b: any) => ({ id: b.id, name: b.name }))}
                regions={regionsList.map((r: any) => ({ id: r.id, name: r.name }))}
                selectedBrandId={selectedBrandId}
                selectedRegionId={selectedRegionId}
                onBrandChange={setSelectedBrandId}
                onRegionChange={setSelectedRegionId}
                onRowClick={(franchisee) => setSelectedFranchisee(franchisee)}
              />
            </Container>
          )}
        </>
      ) : null}

      {/* Franchise Detail Dialog */}
      <FranchiseDetailDialog
        open={!!selectedFranchisee}
        franchisee={selectedFranchisee}
        onClose={() => setSelectedFranchisee(null)}
      />
    </Box>
  );
};
